import { EventType } from "@/analytics/analytics-events";
import {
  computeToolPropertyForSelectLocationEvent,
  SelectLocationProperties,
  SelectLocationSources,
} from "@/analytics/generic-events-properties";
import {
  MapUserMarker,
  type MapUserRendererProps,
} from "@/components/r3f/renderers/map-user-marker-renderer";
import { useCanvasBackground } from "@/hooks/use-canvas-background";
import {
  centerOrthoCamera,
  useCameraRotationFor2dOverviewAndMiniMap,
  useCenterCameraOnPlaceholders,
} from "@/hooks/use-center-camera-on-placeholders";
import { obbToPlanes } from "@/hooks/use-clipping-planes";
import { useCurrentAreaClippingBox } from "@/hooks/use-object-bounding-box";
import { IElementImg } from "@/hooks/use-placeholders";
import { useCurrentScene } from "@/modes/mode-data-context";
import { useDetectEditWaypointsMount } from "@/modes/sheet-mode/edit-waypoints/use-detect-edit-waypoints-mount";
import { SheetModeRenderOrders } from "@/modes/sheet-mode/sheet-mode-render-orders";
import { SheetSceneBase } from "@/modes/sheet-mode/sheet-scene-base";
import { useCached3DObjectsIfExists } from "@/object-cache";
import {
  selectIsMinimapEditing,
  setMiniMapIsEditing,
} from "@/store/minimap-slice";
import { selectFlyWalkSceneFilter } from "@/store/modes/flywalk-mode-slice";
import {
  selectCompareElement,
  selectWalkCompareSceneFilter,
} from "@/store/modes/walk-mode-slice";
import { selectActiveElement } from "@/store/selections-selectors";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { IElementPhotogrammetryScan } from "@/types/ielement-extra-types";
import { SceneFilter } from "@/types/scene-filter";
import {
  CameraAnimationTimeOptions,
  getCameraAnimationTime,
} from "@/utils/camera-animation-time";
import { atEyeLevel } from "@/utils/camera-transform";
import {
  findClosestIndex,
  selectAncestor,
  selectChildrenDepthFirst,
  selectIElementWorldPosition,
  useNonExhaustiveEffect,
  useTypedEvent,
  View,
} from "@faro-lotv/app-component-toolbox";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  isIElementImg2d,
  isIElementImg360,
  isIElementRoomSection,
  isIElementTimeSeriesRoom,
  type IElement,
  type IElementGenericOdometryPath,
} from "@faro-lotv/ielement-types";
import { ObjectPropertyAnimation } from "@faro-lotv/lotv";
import {
  ThreeEvent,
  useFrame,
  Vector3 as Vector3Prop,
} from "@react-three/fiber";
import { isEqual } from "es-toolkit";
import {
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  type JSX,
} from "react";
import { Camera, OrthographicCamera, Vector3, Vector3Tuple } from "three";
import { CameraAnimation } from "../../animations/camera-animation";
import type { PathWaypointTarget } from "../../renderers/odometry-paths/odometry-path-renderer";

/** Animation time to use for the minimap */
const ANIMATION_TIME: CameraAnimationTimeOptions = {
  min: 0.5,
  max: 1,
  scale: 10,
};

/** Minimum height of the minimap orthocamera frustum, when the minimap starts up */
const MIN_MINIMAP_CAMERA_HEIGHT = 28;

/** Actions that can be triggered on the minimap from outside the component */
export type MiniMapActions = {
  /** Centers the minimap camera on a position */
  centerCameraOn(pos: Vector3): void;
};

export type MiniMapProps = {
  /** Id of the ViewDiv this minimap need to track */
  trackingElement: HTMLDivElement;

  /** The canvas inside the minimap div */
  canvasElement?: HTMLCanvasElement | null;

  /** The camera used to show the "user" position on the minimap */
  camera: Camera;

  /** The camera used to show the "user" position on the minimap in second screen (used in split-screen view) */
  secondScreenCamera?: Camera;

  /** The desired camera position, if changed will animate the current camera to this position */
  cameraPosition: Vector3Prop;

  /** True to show the user marker */
  showUserMarker?: boolean;

  /** True if the view cone of the user marker should be visible @default false */
  shouldShowViewCone?: boolean;

  /** True if the view cone related to view in second split-screen should be visible @default false */
  shouldShowViewConeSecondCamera?: boolean;

  /** The starting zoom level of the minimap as a percentage of the bounds of the placeholders */
  zoom?: number;

  /** A handle on actions that can be executed on the minimap */
  actions?: RefObject<MiniMapActions | null>;

  /** Whether the minimap should load views of the active sheets @default false */
  enableSheetViews?: boolean;

  /** Callback when the minimap is clicked which gives the clicked position */
  onMinimapClicked?(pos: Vector3): void;

  /** Callback to signal a placeholder have been clicked*/
  onPlaceholderClicked?(target: IElementImg | IElementPhotogrammetryScan): void;

  /** Callback when a trajectory waypoint without a pano is clicked, with its eye-level position. Falls back to onMinimapClicked */
  onTrajectoryPointClicked?(eyePosition: Vector3): void;
};

/**
 * @returns A component to render a mini map
 */
export function MiniMap({
  trackingElement,
  canvasElement,
  camera,
  cameraPosition,
  secondScreenCamera,
  showUserMarker,
  shouldShowViewCone = false,
  shouldShowViewConeSecondCamera = false,
  actions,
  zoom = 0.3,
  onMinimapClicked,
  onPlaceholderClicked,
  onTrajectoryPointClicked: onTrajectoryEyePositionClicked,
  enableSheetViews = false,
}: MiniMapProps): JSX.Element {
  const {
    activeSheets,
    panos,
    photos,
    paths,
    orthomosaics,
    referenceElement,
    locationMarkers,
    activeSheetFor2DNavigation,
  } = useCurrentScene();

  const cameraRotation = useCameraRotationFor2dOverviewAndMiniMap();
  const cameraData = useCenterCameraOnPlaceholders({
    sheetElements: activeSheets,
    cameraRotation,
    placeholders: panos,
    paddingFactor: zoom,
    cameraPosition,
    minFrustumHeight: MIN_MINIMAP_CAMERA_HEIGHT,
  });

  // Create custom ortho camera
  const [minimapCamera] = useState(() => new OrthographicCamera());

  // Do not change the zoom level every time the active element changes
  useNonExhaustiveEffect(
    () => centerOrthoCamera(minimapCamera, cameraData),
    [minimapCamera],
  );

  // Follow orientation changes (e.g. option or displayed sheet) without changing the zoom level
  useEffect(() => {
    minimapCamera.quaternion.copy(cameraData.quaternion);
  }, [minimapCamera, cameraData.quaternion]);

  // Target position for the animations
  const [cameraTarget, setCameraTarget] = useState<Vector3>();

  // The sheet mode currently only clips using an area's volume, since the user has no way to edit a custom one.
  const areaBox = useCurrentAreaClippingBox();
  const clippingPlanes = useMemo(
    () => (areaBox ? obbToPlanes(areaBox) : undefined),
    [areaBox],
  );

  const background = useCanvasBackground();

  const dispatch = useAppDispatch();

  const isEditing = useAppSelector(selectIsMinimapEditing);
  useDetectEditWaypointsMount(isEditing);
  useEffect(
    // This is a cleanup function that should disable the edit mode on unmount. (eslint requires this unusual formatting)
    () => () => {
      dispatch(setMiniMapIsEditing(false));
    },
    [dispatch],
  );

  useImperativeHandle(actions, () => ({
    centerCameraOn(pos: Vector3) {
      if (!cameraTarget) {
        pos.y = minimapCamera.position.y;
        setCameraTarget(pos);
      }
    },
  }));

  const store = useAppStore();

  const triggerClickOnCloserPlaceholder = useCallback(
    (ev: ThreeEvent<MouseEvent>, path: IElementGenericOdometryPath) => {
      ev.stopPropagation();
      if (!onPlaceholderClicked) return;
      const panos = selectChildrenDepthFirst(
        path,
        isIElementImg360,
      )(store.getState());
      const panoPositions = panos.map((pano) =>
        new Vector3().fromArray(
          selectIElementWorldPosition(pano.id)(store.getState()),
        ),
      );
      const closest = findClosestIndex(ev.point, panoPositions);
      if (closest) {
        onPlaceholderClicked(panos[closest]);
      }
    },
    [onPlaceholderClicked, store],
  );

  const sheetObjects = useCached3DObjectsIfExists(activeSheets, "Minimap");

  const tool = useAppSelector(selectActiveTool);

  const trackSelectLocation = useCallback(() => {
    Analytics.track<SelectLocationProperties>(EventType.selectLocation, {
      via: SelectLocationSources.minimap,
      tool: computeToolPropertyForSelectLocationEvent(tool),
    });
  }, [tool]);

  const trackClickOnPlaceholder = useCallback(
    (ele: IElementImg | IElementPhotogrammetryScan) => {
      // Ignore clicks on location markers
      if (isIElementRoomSection(ele)) return;

      trackSelectLocation();
      onPlaceholderClicked?.(ele);
    },
    [onPlaceholderClicked, trackSelectLocation],
  );

  const sheetForElevation = activeSheetFor2DNavigation ?? activeSheets[0];

  const activeElement = useAppSelector(selectActiveElement);
  const compareElement = useAppSelector(selectCompareElement);

  const sceneFilter = useAppSelector(selectFlyWalkSceneFilter);
  const compareFilter = useAppSelector(selectWalkCompareSceneFilter);

  // When the user indicator position changes, we want to move the minimap
  // camera to be on top of the user indicator
  const onIndicatorPositionChanged = useCallback(
    (position: Vector3Tuple | undefined) => {
      if (position && !cameraTarget) {
        const cameraPosition = new Vector3().fromArray(position);
        cameraPosition.y = minimapCamera.position.y;
        setCameraTarget(cameraPosition);
      }
    },
    [cameraTarget, minimapCamera.position.y],
  );

  // When clicking on a plain OdometryPath, move the camera to the waypoint at its capture
  // elevation, otherwise imitate what happens when the user clicks on the minimap
  const onTrajectoryPointClicked = useCallback(
    ({ path, position, walkPosition }: PathWaypointTarget) => {
      trackSelectLocation();
      if (onTrajectoryEyePositionClicked) {
        onTrajectoryEyePositionClicked(
          atEyeLevel(
            store.getState().iElements,
            path,
            walkPosition ?? position,
          ),
        );
      } else {
        onMinimapClicked?.(position);
      }
    },
    [
      onMinimapClicked,
      onTrajectoryEyePositionClicked,
      store,
      trackSelectLocation,
    ],
  );

  return (
    <View
      trackingElement={trackingElement}
      canvasElement={canvasElement}
      camera={minimapCamera}
      hasSeparateScene
      background={background}
    >
      {cameraTarget && (
        <CameraAnimation
          position={cameraTarget}
          onAnimationFinished={() => setCameraTarget(undefined)}
          duration={getCameraAnimationTime(
            camera,
            cameraPosition,
            ANIMATION_TIME,
          )}
        />
      )}
      <SheetSceneBase
        sheets={sheetObjects}
        sheetElementForElevation={sheetForElevation}
        referenceElement={referenceElement}
        paths={paths}
        panos={panos}
        photos={photos}
        orthomosaics={orthomosaics}
        locationMarkers={locationMarkers}
        clippingPlanes={clippingPlanes}
        isInMinimap
        onSheetClick={onMinimapClicked}
        onTrajectoryPointClicked={onTrajectoryPointClicked}
        onPlaceholderClicked={trackClickOnPlaceholder}
        onPathActivated={triggerClickOnCloserPlaceholder}
        isWaypointMoveActive={isEditing}
        enableSheetViews={enableSheetViews}
      />
      {showUserMarker && !isEditing && (
        <group renderOrder={SheetModeRenderOrders.Waypoints}>
          <UserMarker
            userCamera={camera}
            sheetForElevation={activeSheetFor2DNavigation}
            shouldShowViewCone={shouldShowViewCone}
            activeElement={activeElement}
            sceneFilter={sceneFilter}
            onIndicatorPositionChanged={onIndicatorPositionChanged}
          />
          {secondScreenCamera && (
            <UserMarker
              userCamera={secondScreenCamera}
              sheetForElevation={activeSheetFor2DNavigation}
              shouldShowViewCone={shouldShowViewConeSecondCamera}
              activeElement={compareElement}
              sceneFilter={compareFilter}
            />
          )}
        </group>
      )}
    </View>
  );
}

type UserMarkerProps = {
  /** The active element of the current view */
  activeElement: IElement | undefined;
  /** The scene filter of the current view */
  sceneFilter: SceneFilter;
  /** The callback executed every time the user indicator animation has finished */
  onIndicatorPositionChanged?(position: Vector3Tuple | undefined): void;
} & Pick<
  MapUserRendererProps,
  "sheetForElevation" | "shouldShowViewCone" | "userCamera"
>;

function UserMarker({
  userCamera,
  activeElement,
  sheetForElevation,
  shouldShowViewCone,
  sceneFilter,
  onIndicatorPositionChanged,
}: UserMarkerProps): JSX.Element {
  const timeSeries = useAppSelector(
    selectAncestor(
      activeElement,
      activeElement && isIElementImg2d(activeElement)
        ? isIElementRoomSection
        : isIElementTimeSeriesRoom,
    ),
  );
  const timeSeriesLocation = useAppSelector(
    selectIElementWorldPosition(timeSeries?.id),
    isEqual,
  );
  const activeLocation = useAppSelector(
    selectIElementWorldPosition(activeElement?.id),
  );

  // We don't really care which camera we are creating here,
  // we just need the position and the quaternion
  const [camera] = useState(() => {
    const c = new OrthographicCamera();
    if (timeSeries) {
      c.position.fromArray(timeSeriesLocation);
    } else {
      c.position.copy(userCamera.position);
    }
    c.quaternion.copy(userCamera.quaternion);
    return c;
  });

  const [targetLocation, setTargetLocation] = useState<Vector3Tuple>();
  const animation = useMemo(
    () =>
      targetLocation
        ? new ObjectPropertyAnimation(
            camera,
            "position",
            camera.position,
            new Vector3().fromArray(targetLocation),
            {
              duration: getCameraAnimationTime(
                camera,
                targetLocation,
                ANIMATION_TIME,
              ),
            },
          )
        : undefined,
    [camera, targetLocation],
  );

  const onAnimationFinished = useCallback(
    () => setTargetLocation(undefined),
    [],
  );
  useTypedEvent(animation?.completed, onAnimationFinished);

  useEffect(() => {
    // If we are not rendering a pano, the camera should always follow the 3D camera.
    // No animation should be triggered internally.
    if (sceneFilter !== SceneFilter.Pano) return;

    // If a timeseries exists, then its position should be the animation's target.
    // Otherwise, let's just use the active element location
    if (timeSeries) {
      setTargetLocation(timeSeriesLocation);
    } else if (activeElement && isIElementImg360(activeElement)) {
      setTargetLocation(activeLocation);
    }
  }, [
    activeElement,
    activeLocation,
    sceneFilter,
    timeSeries,
    timeSeriesLocation,
  ]);

  useEffect(() => {
    onIndicatorPositionChanged?.(targetLocation);
  }, [onIndicatorPositionChanged, targetLocation]);

  useFrame((_, delta) => {
    if (animation) {
      animation.startIfNeeded();
      animation.update(delta);
    } else if (sceneFilter !== SceneFilter.Pano) {
      // PointCloud / CAD we should just follow the user camera
      camera.position.copy(userCamera.position);
    } else if (timeSeries) {
      // For rooms or 2d images, we use the timeseries position
      camera.position.fromArray(timeSeriesLocation);
    } else if (activeElement && isIElementImg360(activeElement)) {
      // For scans, we use the active element position
      camera.position.fromArray(activeLocation);
    }
    // Always keep the camera orientation in sync
    camera.quaternion.copy(userCamera.quaternion);
  });

  return (
    <MapUserMarker
      userCamera={camera}
      sheetForElevation={sheetForElevation}
      shouldShowViewCone={shouldShowViewCone}
    />
  );
}
